import { API_CONFIG } from '../constants';
import { store } from '../store';

export interface UploadResult {
  url: string;
  fileName: string;
  attachmentFolderId?: string;
}

export function generateAttachmentFolderId(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function extractFolderIdFromAttachmentUrl(url: string | null | undefined): string | null {
  if (!url || typeof url !== 'string') return null;
  const match = url.match(/bid-attachments\/([^/]+)/);
  return match ? match[1] : null;
}

export function isPublicAttachmentUrl(url: string | null | undefined): boolean {
  if (!url || typeof url !== 'string') return false;
  return url.startsWith('https://') && url.includes('storage.googleapis.com');
}

function attachmentUrlLooksSigned(url: string): boolean {
  return /X-Goog-Algorithm=/i.test(url) || /X-Goog-Signature=/i.test(url);
}

/**
 * Same contract as mobile: use read-url when the API exposes it; if the route is missing (404 without our JSON
 * error body), fall back to `fileUrl` so behavior matches pre-read-url deployments until main is fully promoted.
 */
export async function getAttachmentReadUrl(fileUrl: string, bidId: string): Promise<string> {
  if (attachmentUrlLooksSigned(fileUrl)) return fileUrl;
  const token = store.getState().auth.token;
  if (!token) throw new Error('Not authenticated');
  const q = encodeURIComponent(fileUrl);
  const b = encodeURIComponent(bidId);
  const res = await fetch(`${API_CONFIG.BASE_URL}/api/attachments/read-url?url=${q}&bidId=${b}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (res.status === 404) {
    const text = await res.text();
    let body: { success?: boolean; error?: string } | null = null;
    try {
      body = text ? (JSON.parse(text) as { success?: boolean; error?: string }) : null;
    } catch {
      body = null;
    }
    if (body?.success === false && typeof body.error === 'string') {
      throw new Error(body.error);
    }
    return fileUrl;
  }

  const data = (await res.json().catch(() => ({}))) as {
    success?: boolean;
    data?: { url?: string };
    error?: string;
  };
  if (!res.ok || !data?.success || !data?.data?.url) {
    throw new Error(data?.error || 'Failed to open attachment');
  }
  return data.data.url;
}

export async function uploadFile(
  file: File,
  attachmentFolderId: string
): Promise<UploadResult> {
  const token = store.getState().auth.token;
  if (!token) throw new Error('Not authenticated');

  const formData = new FormData();
  formData.append('attachmentFolderId', attachmentFolderId);
  formData.append('file', file, file.name);

  const res = await fetch(`${API_CONFIG.BASE_URL}/api/attachments/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  const data = await res.json();
  if (!res.ok || !data?.success || !data?.data?.url) {
    throw new Error(data?.error || 'Failed to upload attachment');
  }
  return {
    url: data.data.url,
    fileName: data.data.fileName ?? file.name,
    attachmentFolderId: data.data.attachmentFolderId,
  };
}
